import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import { User, Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

// Password strength indicator
const PasswordStrength = ({ password }) => {
  const getStrength = () => {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    return score;
  };

  const strength = getStrength();
  const getColor = () => {
    if (strength <= 2) return 'bg-red-500';
    if (strength <= 4) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getLabel = () => {
    if (strength <= 2) return 'Weak';
    if (strength <= 4) return 'Medium';
    return 'Strong';
  };

  if (!password) return null;

  return (
    <div className="mt-2">
      <div className="flex gap-1 mb-1">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded ${
              i <= strength ? getColor() : 'bg-muted'
            }`}
          />
        ))}
      </div>
      <span className="text-xs text-muted-foreground">Password strength: {getLabel()}</span>
    </div>
  );
};

export const Login = () => {
  usePageMeta('Log In or Sign Up', 'Log in to SoundWolves or create your account to book tickets, follow DJs and unlock VIP experiences.');
  const navigate = useNavigate();
  const { login, register, error: authError, isAuthenticated, loading } = useAuth();

  // Everyone — admin, DJ, organizer, normal user — lands on the homepage after
  // auth. Role-based options then surface in the navbar/account menu. We do NOT
  // auto-route to role dashboards (/admin, /dj-dashboard) or account pages.
  const HOME = '/';

  // Loop-safe: if an already-authenticated user opens /login, send them home
  // instead of re-showing the form. Guarded on `loading` so we never redirect
  // before the initial auth check resolves.
  useEffect(() => {
    if (!loading && isAuthenticated) navigate(HOME, { replace: true });
  }, [loading, isAuthenticated, navigate]);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [signupData, setSignupData] = useState({ name: '', email: '', password: '' });

  // Validate password requirements
  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(password)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('One lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('One number');
    return errors;
  };

  // Validate email format
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setValidationErrors({});
    setIsLoading(true);

    try {
      if (!validateEmail(loginData.email)) {
        setValidationErrors({ email: 'Please enter a valid email address' });
        return;
      }

      await login(loginData.email, loginData.password);
      // Single, non-intrusive success alert shown for every successful login
      // (user / DJ / admin all funnel through the same login() function above).
      // Fires only after login resolves — never on failure — and survives the
      // redirect below because <Toaster /> is mounted at the app root.
      toast.success('You’re now logged in.');
      navigate(HOME);   // everyone lands on the homepage, regardless of role
    } catch (err) {
      toast.error(err.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setValidationErrors({});
    setIsLoading(true);

    try {
      // Validate inputs (same rules as before)
      const errors = {};
      if (!signupData.name || signupData.name.length < 2) {
        errors.name = 'Name must be at least 2 characters';
      }
      if (!validateEmail(signupData.email)) {
        errors.email = 'Please enter a valid email address';
      }
      const passwordErrors = validatePassword(signupData.password);
      if (passwordErrors.length > 0) {
        errors.password = `Password requires: ${passwordErrors.join(', ')}`;
      }

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      }

      await register(signupData.name, signupData.email, signupData.password, 'user');
      toast.success('Account created successfully!');
      navigate(HOME);   // new accounts also land on the homepage
    } catch (err) {
      toast.error(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const ErrorMessage = ({ field }) => {
    const error = validationErrors[field];
    if (!error) return null;
    return (
      <div className="flex items-center gap-1 mt-1 text-sm text-destructive">
        <AlertCircle className="w-4 h-4" />
        <span>{error}</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen pt-20 pb-24 md:pb-8 flex items-center justify-center">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-8">
          <img
            src="/logo.png"
            alt="SoundWolves Logo"
            className="h-16 mx-auto mb-4 object-contain"
          />
          <h1 className="text-4xl sm:text-5xl font-display font-bold mb-3">
            Welcome to <span className="text-gradient-gold">SoundWolves</span>
          </h1>
          <p className="text-muted-foreground text-lg">
            Log in or create your account to access exclusive events and VIP experiences
          </p>
        </div>

        {authError && (
          <div className="max-w-md mx-auto mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              <span>{authError}</span>
            </div>
          </div>
        )}

        <Card className="border-border/50 max-w-md mx-auto">
          <Tabs defaultValue="login">
            <CardHeader>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">Log In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>
            </CardHeader>

            <CardContent>
              {/* Log In — existing users */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="your@email.com"
                        className="pl-10"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                        autoComplete="email"
                        disabled={isLoading}
                      />
                    </div>
                    <ErrorMessage field="email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        className="pl-10 pr-10"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                        autoComplete="current-password"
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" variant="premium" className="w-full" size="lg" disabled={isLoading}>
                    {isLoading ? 'Signing in...' : 'Log In'}
                  </Button>
                </form>
              </TabsContent>

              {/* Sign Up — new users */}
              <TabsContent value="signup">
                <form onSubmit={handleSignup} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="signup-name"
                        type="text"
                        placeholder="John Doe"
                        className="pl-10"
                        value={signupData.name}
                        onChange={(e) => setSignupData({ ...signupData, name: e.target.value })}
                        required
                        minLength={2}
                        maxLength={100}
                        autoComplete="name"
                        disabled={isLoading}
                      />
                    </div>
                    <ErrorMessage field="name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="signup-email"
                        type="email"
                        placeholder="your@email.com"
                        className="pl-10"
                        value={signupData.email}
                        onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                        required
                        autoComplete="email"
                        disabled={isLoading}
                      />
                    </div>
                    <ErrorMessage field="email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="signup-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Create a strong password"
                        className="pl-10 pr-10"
                        value={signupData.password}
                        onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                        required
                        minLength={8}
                        autoComplete="new-password"
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <PasswordStrength password={signupData.password} />
                    <ErrorMessage field="password" />
                    <p className="text-xs text-muted-foreground mt-1">
                      Password must be at least 8 characters with uppercase, lowercase, and numbers
                    </p>
                  </div>
                  <Button type="submit" variant="premium" className="w-full" size="lg" disabled={isLoading}>
                    {isLoading ? 'Creating account...' : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>

        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
